// src/pages/RegistroSegnalazioniPage.js
// Vista gestionale aggregata di tutte le Segnalazioni Cliente dell'organizzazione — nessuna
// creazione qui (si crea solo da Personale → Segnalazioni Cliente), solo consultazione,
// filtri e presa in carico (assegnazione / data scadenza / stato).
import React, { useState, useEffect, useMemo } from "react";
import { esportaXLSX } from "../utils/esportaXLSX";
import { collection, query, where, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { Search, Megaphone } from "lucide-react";
import { SegnalazioneForm, STATI_SEG, PRIORITA } from "./SegnalazioneClientePage";
import { toLocalDateStr } from "../utils/dateUtils";
import { etichettaCommessa, giorniGestione } from "../utils/segnalazioniCliente";
const escH = (v) => String(v ?? "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");

const inp = {padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none"};

const PRIORITA_COL = { Bassa:"#6b7280", Media:"#d97706", Alta:"#dc2626" };

export default function RegistroSegnalazioniPage({ deepLink }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const canAdmin = isOrgAdmin || isPlatformOwner;

  const [segnalazioni, setSegnalazioni] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [editSeg, setEditSeg] = useState(null);
  const [orgInfo, setOrgInfo] = useState(null);

  const [cerca, setCerca] = useState("");
  const [filtroStato, setFiltroStato] = useState("");
  const [filtroPriorita, setFiltroPriorita] = useState("");
  const [filtroCliente, setFiltroCliente] = useState(deepLink?.clienteId || "");
  const [filtroCommessa, setFiltroCommessa] = useState(deepLink?.projectId || "");
  // Se si arriva qui da "Relazioni della Commessa" o dalla Scheda Cliente, precompila il filtro
  useEffect(() => { if (deepLink?.projectId) setFiltroCommessa(deepLink.projectId); }, [deepLink?.projectId]);
  useEffect(() => { if (deepLink?.clienteId) setFiltroCliente(deepLink.clienteId); }, [deepLink?.clienteId]);
  const [filtroUtente, setFiltroUtente] = useState("");
  const [filtroPM, setFiltroPM] = useState("");
  const [soloMie, setSoloMie] = useState(false);
  const [filtroDataDa, setFiltroDataDa] = useState("");
  const [filtroDataA, setFiltroDataA] = useState("");

  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"segnalazioniCliente"),where("orgId","==",effectiveOrgId)),
      snap=>setSegnalazioni(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0))));
  },[effectiveOrgId]);

  // Tempo medio di gestione — dalla creazione all'ultimo salvataggio della segnalazione
  // chiusa (updatedAt coincide quasi sempre con la chiusura, dato che si aggiorna a ogni
  // modifica). Nessun dato nuovo da compilare, solo quello che già esiste.
  const tempoMedioGestione = useMemo(() => {
    // dalla registrazione alla chiusura (chiusaData, W409; per le vecchie l'ultimo salvataggio)
    const giorni = segnalazioni.filter(s => s.stato==="chiusa").map(giorniGestione).filter(g => g !== null);
    if (giorni.length===0) return null;
    return { media: giorni.reduce((a,b)=>a+b,0)/giorni.length, n: giorni.length };
  }, [segnalazioni]);

  useEffect(()=>{
    if(!effectiveOrgId) return;
    onSnapshot(query(collection(db,"projects"),where("organizationId","==",effectiveOrgId)),
      snap=>setCommesse(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.archived&&!p.isParent)));
    onSnapshot(query(collection(db,"clienti"),where("organizationId","==",effectiveOrgId)),
      snap=>setClienti(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.ragioneSociale||"").localeCompare(b.ragioneSociale||"","it"))));
    onSnapshot(query(collection(db,"users"),where("organizationId","==",effectiveOrgId),where("enabled","==",true)),
      snap=>{ const l=snap.docs.map(d=>d.data()); l.sort((a,b)=>(a.displayName||"").localeCompare(b.displayName||"","it")); setUtenti(l); });
  },[effectiveOrgId]);

  useEffect(()=>{
    if(!effectiveOrgId) return;
    getDoc(doc(db,"organizations",effectiveOrgId)).then(s=>{if(s.exists())setOrgInfo(s.data());});
  },[effectiveOrgId]);

  // PM per commessa — derivato dalla commessa stessa, nessun dato nuovo (responsabileNome già esistente)
  const pmPerCommessa = useMemo(()=>{
    const m = {}; commesse.forEach(c=>{ m[c.id]=c.responsabileNome||""; }); return m;
  },[commesse]);
  const pmDisponibili = useMemo(()=>[...new Set(commesse.map(c=>c.responsabileNome).filter(Boolean))].sort((a,b)=>a.localeCompare(b,"it")),[commesse]);

  const oggi = toLocalDateStr(new Date());

  const filtrate = segnalazioni.filter(s=>{
    if(soloMie && s.assegnatarioUid!==currentUser?.uid) return false;
    if(filtroStato && s.stato!==filtroStato) return false;
    if(filtroPriorita && s.priorita!==filtroPriorita) return false;
    if(filtroCliente && s.clienteId!==filtroCliente) return false;
    if(filtroCommessa && s.projectId!==filtroCommessa) return false;
    if(filtroUtente && s.compilatoreUid!==filtroUtente) return false;
    if(filtroPM && (pmPerCommessa[s.projectId]||"")!==filtroPM) return false;
    if(filtroDataDa && (!s.dataRicezione || s.dataRicezione<filtroDataDa)) return false;
    if(filtroDataA && (!s.dataRicezione || s.dataRicezione>filtroDataA)) return false;
    if(cerca){
      const q=cerca.toLowerCase();
      return (s.numero||"").toLowerCase().includes(q) || (s.oggetto||"").toLowerCase().includes(q) || (s.descrizione||"").toLowerCase().includes(q) ||
        (s.clienteNome||"").toLowerCase().includes(q) || (s.projectName||"").toLowerCase().includes(q);
    }
    return true;
  });

  const resetFiltri = () => { setCerca("");setFiltroStato("");setFiltroPriorita("");setFiltroCliente("");setFiltroCommessa("");setFiltroUtente("");setFiltroPM("");setSoloMie(false);setFiltroDataDa("");setFiltroDataA(""); };
  const filtriAttivi = cerca||filtroStato||filtroPriorita||filtroCliente||filtroCommessa||filtroUtente||filtroPM||soloMie||filtroDataDa||filtroDataA;

  const stampaPDF = () => {
    const win = window.open("","_blank");
    const logoHtml = orgInfo?.logo ? `<img src="${orgInfo.logo}" style="height:36px;object-fit:contain;"/>` :
      `<div style="font-size:16px;font-weight:900;color:#1e1b4b;">◆ ${orgInfo?.nome||orgInfo?.name||""}</div>`;
    const righe = filtrate.map(s => `
      <tr>
        <td style="font-family:monospace;font-weight:700;color:#7c3aed">${s.numero||"—"}</td>
        <td>${s.dataRicezione?.split("-").reverse().join("/")||"—"}</td>
        <td>${s.canale||"—"}</td>
        <td>${s.clienteNome||"—"}</td>
        <td>${s.oggetto||"—"}</td>
        <td>${escH(etichettaCommessa(s)||"—")}</td>
        <td>${s.priorita||"—"}</td>
        <td>${STATI_SEG[s.stato]?.label||s.stato||"—"}${s.stato==="chiusa"&&s.esito?`<div style="font-size:9px;color:#065f46">${escH(s.esito)}</div>`:""}</td>
        <td>${s.assegnatarioNome||"—"}</td>
        <td>${s.dataScadenza?.split("-").reverse().join("/")||"—"}</td>
      </tr>`).join("");
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Registro Segnalazioni Cliente</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;font-size:11px;color:#111;padding:12mm 16mm;}
      @media screen{body{max-width:1050px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4 landscape;}
      @media print{.no-print{display:none;}}
      .hdr{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #1e1b4b;padding-bottom:10px;margin-bottom:14px;}
      table{width:100%;border-collapse:collapse;}
      th{background:#1e1b4b;color:#fff;padding:6px 8px;font-size:10px;text-align:left;text-transform:uppercase;}
      td{padding:5px 8px;border-bottom:1px solid #e5e7eb;font-size:11px;}
    </style></head><body>
      <div class="hdr">
        <div>${logoHtml}<div style="font-size:10px;color:#6b7280;margin-top:3px;">${orgInfo?.nome||orgInfo?.name||""}</div></div>
        <div style="text-align:right;">
          <div style="font-size:15px;font-weight:800;color:#1e1b4b;">📣 REGISTRO SEGNALAZIONI CLIENTE</div>
          <div style="font-size:10px;color:#6b7280;margin-top:2px;">Generato il ${new Date().toLocaleDateString("it-IT")} — ${filtrate.length} segnalazione/i</div>
        </div>
      </div>
      <table>
        <thead><tr><th>Numero</th><th>Data ricezione</th><th>Canale</th><th>Cliente</th><th>Oggetto</th><th>Commessa</th><th>Priorità</th><th>Stato</th><th>Assegnatario</th><th>Scadenza</th></tr></thead>
        <tbody>${righe}</tbody>
      </table>
    </body></html>`);
    win.document.close();
    setTimeout(() => {
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print";
      btn.style.cssText = "position:fixed;top:16px;right:16px;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(124,58,237,.3);z-index:9999;";
      btn.onclick = () => win.print();
      win.document.body.appendChild(btn);
    }, 50);
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"16px 24px",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <div style={{display:"flex",alignItems:"center",gap:14}}>
            <div>
              <h2 style={{fontSize:18,fontWeight:800,color:"#1e1b4b",margin:0,display:"flex",alignItems:"center",gap:8}}>
                <Megaphone size={18}/> Registro Segnalazioni Cliente
              </h2>
              <p style={{fontSize:12,color:"#9ca3af",margin:"2px 0 0"}}>{filtrate.length} segnalazione/i · Vista gestionale — la creazione avviene solo da Personale → Segnalazioni Cliente</p>
            </div>
            {tempoMedioGestione && (
              <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:"6px 14px",flexShrink:0}}>
                <div style={{fontSize:9,color:"#7c3aed",fontWeight:700,textTransform:"uppercase"}}>Tempo medio gestione</div>
                <div style={{fontSize:16,fontWeight:800,color:"#5b21b6"}}>{tempoMedioGestione.media.toFixed(1)}gg <span style={{fontSize:10,fontWeight:600,color:"#9ca3af"}}>su {tempoMedioGestione.n} chiuse</span></div>
              </div>
            )}
            <button onClick={()=>{
              const intestazione=["Numero","Data ricezione","Canale","Cliente","Oggetto","Commessa","Priorità","Stato","Esito","Assegnatario","Scadenza","Compilato da"];
              const righe=filtrate.map(s=>[s.numero||"—",s.dataRicezione?new Date(s.dataRicezione+"T00:00:00").toLocaleDateString("it-IT"):"—",s.canale||"—",s.clienteNome||"—",s.oggetto||"—",etichettaCommessa(s)||"—",s.priorita||"—",STATI_SEG[s.stato]?.label||s.stato,s.esito||"",s.assegnatarioNome||"—",s.dataScadenza?new Date(s.dataScadenza+"T00:00:00").toLocaleDateString("it-IT"):"—",s.compilatoreNome||"—"]);
              esportaXLSX([intestazione,...righe],`Registro_Segnalazioni_${new Date().toLocaleDateString("it-IT").replace(/\//g,"-")}`,"Segnalazioni");
            }} style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",border:"1px solid #059669",borderRadius:7,background:"#f0fdf4",color:"#059669",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",flexShrink:0}}>
              📊 Excel
            </button>
            <button onClick={stampaPDF}
              style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",border:"1px solid #7c3aed",borderRadius:7,background:"#faf5ff",color:"#7c3aed",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",flexShrink:0}}>
              🖨️ Stampa PDF
            </button>
          </div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <div style={{position:"relative"}}>
            <Search size={13} style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)",color:"#9ca3af"}}/>
            <input value={cerca} onChange={e=>setCerca(e.target.value)} placeholder="Cerca numero, descrizione, cliente..." style={{...inp,paddingLeft:26,width:220}}/>
          </div>
          <button onClick={()=>setSoloMie(v=>!v)}
            style={{padding:"6px 12px",border:"1px solid "+(soloMie?"#d97706":"#e5e7eb"),borderRadius:8,background:soloMie?"#fef3c7":"#fff",color:soloMie?"#92400e":"#374151",fontSize:12,fontWeight:600,cursor:"pointer"}}>
            📤 Assegnate a me
          </button>
          <select value={filtroStato} onChange={e=>setFiltroStato(e.target.value)} style={inp}>
            <option value="">Tutti gli stati</option>
            {Object.entries(STATI_SEG).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filtroPriorita} onChange={e=>setFiltroPriorita(e.target.value)} style={inp}>
            <option value="">Tutte le priorità</option>
            {PRIORITA.map(p=><option key={p} value={p}>{p}</option>)}
          </select>
          <select value={filtroCliente} onChange={e=>setFiltroCliente(e.target.value)} style={inp}>
            <option value="">Tutti i clienti</option>
            {clienti.map(c=><option key={c.id} value={c.id}>{c.ragioneSociale}</option>)}
          </select>
          <select value={filtroCommessa} onChange={e=>setFiltroCommessa(e.target.value)} style={inp}>
            <option value="">Tutte le commesse</option>
            {commesse.map(c=><option key={c.id} value={c.id}>{c.codiceCommessa?`[${c.codiceCommessa}] `:""}{c.name}</option>)}
          </select>
          <select value={filtroUtente} onChange={e=>setFiltroUtente(e.target.value)} style={inp}>
            <option value="">Chi ha registrato</option>
            {utenti.map(u=><option key={u.uid} value={u.uid}>{u.displayName}</option>)}
          </select>
          <select value={filtroPM} onChange={e=>setFiltroPM(e.target.value)} style={inp}>
            <option value="">PM commessa</option>
            {pmDisponibili.map(p=><option key={p} value={p}>{p}</option>)}
          </select>
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <span style={{fontSize:11,color:"#9ca3af"}}>Ricezione dal</span>
            <input type="date" value={filtroDataDa} onChange={e=>setFiltroDataDa(e.target.value)} style={inp}/>
            <span style={{fontSize:11,color:"#9ca3af"}}>al</span>
            <input type="date" value={filtroDataA} onChange={e=>setFiltroDataA(e.target.value)} style={inp}/>
          </div>
          {filtriAttivi && (
            <button onClick={resetFiltri} style={{padding:"6px 12px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",fontSize:12,cursor:"pointer"}}>× Reset</button>
          )}
        </div>
      </div>

      <div style={{flex:1,overflow:"auto"}}>
        {filtrate.length===0 ? (
          <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>
            <div style={{fontSize:48,marginBottom:12,opacity:.15}}>📣</div>
            <div style={{fontSize:14,fontWeight:600}}>Nessuna segnalazione</div>
          </div>
        ):(
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead style={{position:"sticky",top:0,zIndex:5}}>
              <tr style={{background:"#1e1b4b"}}>
                {["N°","Data","Canale","Cliente","Oggetto","Commessa","Priorità","Stato","Assegnatario","Scadenza","Compilato da"].map(h=>(
                  <th key={h} style={{padding:"9px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrate.map((s,i)=>{
                const stato = STATI_SEG[s.stato]||STATI_SEG.nuova;
                const scaduta = s.dataScadenza && s.dataScadenza<oggi && s.stato!=="chiusa";
                return (
                  <tr key={s.id} style={{background:i%2===0?"#fff":"#fafafa",borderBottom:"1px solid #f3f4f6",cursor:"pointer"}} onClick={()=>setEditSeg(s)}>
                    <td style={{padding:"9px 12px",fontFamily:"monospace",fontWeight:800,color:"#7c3aed"}}>{s.numero||"—"}</td>
                    <td style={{padding:"9px 12px",color:"#6b7280",whiteSpace:"nowrap"}}>{s.dataRicezione?new Date(s.dataRicezione+"T00:00:00").toLocaleDateString("it-IT"):"—"}</td>
                    <td style={{padding:"9px 12px",color:"#374151"}}>{s.canale||"—"}</td>
                    <td style={{padding:"9px 12px",fontWeight:600,color:"#111827"}}>{s.clienteNome||"—"}</td>
                    <td style={{padding:"9px 12px",color:"#374151",maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {s.oggetto||"—"}{s.allegati?.length>0 && <span style={{marginLeft:5,color:"#7c3aed"}} title={`${s.allegati.length} allegato/i`}>📎{s.allegati.length}</span>}
                    </td>
                    <td style={{padding:"9px 12px",color:"#7c3aed",fontFamily:"monospace",fontSize:11}}>
                      {s.projectName ? <>{s.codiceCommessa?`[${s.codiceCommessa}] `:""}{s.projectName}</> : <span style={{color:"#d1d5db"}}>—</span>}
                    </td>
                    <td style={{padding:"9px 12px",fontWeight:700,color:PRIORITA_COL[s.priorita]||"#6b7280"}}>{s.priorita||"—"}</td>
                    <td style={{padding:"9px 12px"}}>
                      <span title={s.stato==="chiusa"&&s.esito?`Esito: ${s.esito}`:undefined} style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:stato.bg,color:stato.col,fontWeight:700}}>{stato.label}</span>
                      {s.stato==="chiusa" && s.esito && <div style={{fontSize:10.5,color:"#065f46",marginTop:3,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.esito}</div>}
                    </td>
                    <td style={{padding:"9px 12px",color:"#d97706",fontWeight:600}}>{s.assegnatarioNome||"—"}</td>
                    <td style={{padding:"9px 12px",fontWeight:600,color:scaduta?"#dc2626":"#6b7280"}}>{s.dataScadenza?new Date(s.dataScadenza+"T00:00:00").toLocaleDateString("it-IT"):"—"}{scaduta&&" ⚠️"}</td>
                    <td style={{padding:"9px 12px",color:"#6b7280"}}>{s.compilatoreNome||"—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editSeg && (
        <SegnalazioneForm seg={editSeg} orgId={effectiveOrgId} currentUser={currentUser}
          utenti={utenti} clienti={clienti} commesseRecenti={[]} canAdmin={canAdmin}
          onClose={()=>setEditSeg(null)}/>
      )}
    </div>
  );
}
